import { Currency, ETHER, Token } from '@uniswap/sdk'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import USDTLogo from '../../assets/images/usdt.png'
import EthereumLogo from '../../assets/images/ethereum-logo.png'
import useHttpLocations from '../../hooks/useHttpLocations'
import { WrappedTokenInfo, useSelectedTokenList } from '../../state/lists/hooks'
import { useActiveWeb3React } from '../../hooks'
import Logo from '../Logo'

const getTokenLogoURL = (address: string) =>
  `https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/assets/${address}/logo.png`

const StyledEthereumLogo = styled.img<{ size: string }>`
  width: ${({ size }) => size};
  height: ${({ size }) => size};
  box-shadow: 0px 6px 10px rgba(0, 0, 0, 0.075);
  border-radius: 24px;
`

const StyledLogo = styled(Logo)<{ size: string }>`
  width: ${({ size }) => size};
  height: ${({ size }) => size};
`

export default function CurrencyLogo({
  currency,
  size = '24px',
  style
}: {
  currency?: Currency
  size?: string
  style?: React.CSSProperties
}) {
  const { chainId } = useActiveWeb3React()
  const allTokensFromList = useSelectedTokenList()

  // 如果当前currency不是WrappedTokenInfo，尝试从token列表中找到对应的token获取logoURI
  const tokenFromList = useMemo(() => {
    if (!currency || currency instanceof WrappedTokenInfo || !chainId) return undefined
    if (!(currency instanceof Token)) return undefined

    const tokensInChain = allTokensFromList[chainId] || {}

    // 尝试多种地址格式查找
    const found =
      tokensInChain[currency.address?.toLowerCase()] ||
      tokensInChain[currency.address] ||
      Object.values(tokensInChain).find(
        (token: any) => token.address?.toLowerCase() === currency.address?.toLowerCase()
      )

    // 调试信息
    if (currency.symbol === 'SYB') {
      console.log('SYB 调试信息:', {
        chainId,
        address: currency.address,
        addressLower: currency.address?.toLowerCase(),
        tokensInChainKeys: Object.keys(tokensInChain),
        foundByLowerCase: tokensInChain[currency.address?.toLowerCase()],
        foundByOriginal: tokensInChain[currency.address],
        foundBySearch: found,
        foundLogoURI: found?.logoURI
      })
    }

    return found
  }, [currency, allTokensFromList, chainId])

  const logoURI = currency instanceof WrappedTokenInfo ? currency.logoURI : tokenFromList?.logoURI
  const uriLocations = useHttpLocations(logoURI)

  // 更多调试信息
  if (currency?.symbol === 'SYB') {
    console.log('SYB CurrencyLogo 调试:', {
      currency,
      isWrappedTokenInfo: currency instanceof WrappedTokenInfo,
      tokenFromList,
      logoURI,
      uriLocations
    })
  }

  const srcs: string[] = useMemo(() => {
    if (currency === ETHER) return []

    if (currency instanceof Token) {
      if (currency instanceof WrappedTokenInfo) {
        const result = [...uriLocations, getTokenLogoURL(currency.address)]
        if (currency.symbol === 'SYB') {
          console.log('SYB WrappedTokenInfo srcs:', result)
        }
        return result
      }

      // 对于普通Token，如果从token列表找到了logoURI，则使用它
      if (uriLocations.length > 0) {
        const result = [...uriLocations, getTokenLogoURL(currency.address)]
        if (currency.symbol === 'SYB') {
          console.log('SYB 普通Token with logoURI srcs:', result)
        }
        return result
      }

      const result = [getTokenLogoURL(currency.address)]
      if (currency.symbol === 'SYB') {
        console.log('SYB 普通Token fallback srcs:', result)
      }
      return result
    }
    return []
  }, [currency, uriLocations])

  if (currency === ETHER || currency?.symbol === 'WMUD') {
    return <StyledEthereumLogo src={EthereumLogo} size={size} style={style} />
  }

  if (currency?.symbol === 'USDT') {
    return <StyledEthereumLogo src={USDTLogo} size={size} style={style} />
  }

  return <StyledLogo size={size} srcs={srcs} alt={`${currency?.symbol ?? 'token'} logo`} style={style} />
}
